/**
 * The industries MLMind serves, one page each at /industries/<slug>, plus
 * the /industries hub. Only industries with shipped work behind them are
 * listed; `projects` names that work (titles from data/projects.js) and the
 * page shows it as proof. An industry without a shipped project simply shows
 * no case-study block — never an invented one.
 *
 * Adding an industry is one object below plus one <url> line in
 * public/sitemap.xml (the prerender warns if it is missing).
 *
 * Content is English only for now; other languages show it as-is.
 *
 * Fields
 *  slug, name       URL segment and short name (menu, cards, breadcrumbs)
 *  title            the page <h1>
 *  seoTitle         <title> without " | MLMind" — keep it ≤ 51 chars
 *  seoDescription   meta description, ~150–160 chars
 *  summary          one or two sentences for the hub and the menu
 *  tagline, lead    hero line and hero paragraph
 *  visual           3D artwork — see components/AbstractVisual
 *  statement, intro "Built for …" headline and two paragraphs
 *  solutions        what we build for this industry
 *  sectors          the kinds of organisations inside it we work with
 *  reasons          why MLMind, for this industry
 *  outcomes         what changes for the client — no numbers we cannot show
 *  projects         shipped work, by project title
 *  services         related service slugs (data/services.js)
 *  faq              questions buyers in this industry ask (FAQPage JSON-LD)
 */

const industries = [
  {
    slug: 'healthcare',
    name: 'Healthcare & Medical',
    title: 'AI and software development for healthcare',
    seoTitle: 'Healthcare AI & Medical Software Development',
    seoDescription:
      'Healthcare software and medical AI from a team that has shipped it: imaging AI for radiology, lab booking portals, clinic marketplaces and patient assistants.',
    summary:
      'Medical imaging AI, booking portals for clinics and labs, and patient-facing assistants — built with the doctor in control.',
    tagline: 'Software that gives clinicians time back, and never takes the decision away.',
    lead:
      'We have built AI that helps radiologists read MRI, CT and X-ray studies, a booking portal for a laboratory network and a medical tourism marketplace. Healthcare is where most of our shipped work lives.',
    visual: { shape: 'orb', colors: ['#0e2a47', '#e7ff02', '#38bdf8'] },
    statement: 'Built for clinics, labs and health-tech teams.',
    intro: [
      'Healthcare organisations do not lack ideas for software — they lack time, clean integrations and a partner who understands that a wrong suggestion in a clinical tool costs more than a slow one. Most of the work sits in unglamorous places: DICOM pipelines, scheduling rules, patient data that has to stay private, and staff who will only use a tool that fits the way they already work.',
      'We design every clinical feature so the system suggests and the professional decides. Models are measured on your own anonymised cases before anyone relies on them, interfaces are tested with the people who will use them, and patient data is handled under GDPR from the first sprint.',
    ],
    solutions: [
      { title: 'Medical imaging AI', text: 'Segmentation, classification and detection on MRI, CT and X-ray studies, shown inside a reading interface so radiologists work through a worklist faster.' },
      { title: 'Booking & lab portals', text: 'Location search, test catalogues and online scheduling for clinic and laboratory networks, connected to the systems branches already use.' },
      { title: 'Clinic marketplaces', text: 'Searchable directories of clinics and specialists with profiles and online booking — including for medical tourism.' },
      { title: 'Clinical document assistants', text: 'Assistants that answer staff questions from protocols, guidelines and internal procedures, with a link to the source on every answer.' },
      { title: 'Patient-facing assistants', text: 'Chat and voice assistants that handle appointments and common questions in Georgian, English or Russian, and hand over to staff when needed.' },
      { title: 'Operational forecasting', text: 'Predicting no-shows, busy days and test volumes from your own history, so staffing and capacity follow demand.' },
    ],
    sectors: [
      { title: 'Private clinics & hospitals', text: 'Patient portals, booking, internal assistants and analytics.' },
      { title: 'Diagnostic & imaging centres', text: 'AI-assisted reading and worklist tools for radiology teams.' },
      { title: 'Laboratory networks', text: 'Branch search, test catalogues and online appointment booking.' },
      { title: 'Medical tourism & marketplaces', text: 'Platforms that connect patients with clinics and specialists abroad.' },
      { title: 'Health-tech startups', text: 'From prototype to production for products that need medical AI or complex scheduling.' },
    ],
    reasons: [
      { title: 'Shipped healthcare products', text: 'MedicMinder, MedLocator and a medical tourism platform are live work, not slides — we have already met the problems your project will meet.' },
      { title: 'Clinician in the loop', text: 'Every AI feature is designed to support a professional’s judgement, with confidence shown and the final decision left to a person.' },
      { title: 'Privacy by design', text: 'Anonymised data for development, EU-region hosting when required, access logs and a data processing agreement before any patient data is shared.' },
      { title: 'Measured on your cases', text: 'Accuracy is reported on a held-out set of your own anonymised studies or records before launch, not on a public benchmark.' },
    ],
    outcomes: [
      { title: 'Faster reporting', text: 'Radiologists start each study with regions of interest already marked, instead of from a blank screen.' },
      { title: 'Fewer phone calls', text: 'Patients find the right branch and book online, so front-desk staff spend less time on the phone.' },
      { title: 'Answers staff can trust', text: 'Protocol and procedure questions answered in seconds, with the source document linked.' },
      { title: 'Capacity that follows demand', text: 'Staffing and slots planned from forecasts instead of last year’s averages.' },
    ],
    projects: ['MedicMinder', 'MedLocator', 'Medical Tourism'],
    services: ['computer-vision', 'web-development', 'rag'],
    faq: [
      {
        question: 'Does medical AI replace the doctor?',
        answer: 'No. Our clinical tools suggest — they mark regions of interest or draft an answer — and a qualified professional reviews and decides. That is both safer and what regulators and clinicians expect.',
      },
      {
        question: 'How do you handle patient data?',
        answer: 'We develop on anonymised data wherever possible, keep production data in EU-region or your own infrastructure, log access, and sign a data processing agreement before any personal data is shared. We design for GDPR and, for US clients, for HIPAA requirements, and work with your compliance officer on sign-off.',
      },
      {
        question: 'Is diagnostic AI a regulated medical device?',
        answer: 'It can be. Under the EU Medical Device Regulation, software intended to support diagnosis may be a medical device. We build with the documentation and traceability certification needs, while the certification itself is the responsibility of the product’s manufacturer — we help you plan for it from the start.',
      },
      {
        question: 'Can you integrate with our existing systems?',
        answer: 'Yes. We work with DICOM/PACS for imaging, and with scheduling, lab and hospital information systems through their APIs or HL7/FHIR interfaces where they exist.',
      },
    ],
  },
  {
    slug: 'architecture-construction',
    name: 'Architecture & Construction',
    title: 'AI and software for architecture and construction',
    seoTitle: 'AI for Architecture, Construction & BIM',
    seoDescription:
      'AI for architecture and construction: 2D drawings converted to BIM, document search across specifications, site photo analysis and project portals.',
    summary:
      'Turning drawings, specifications and site photos into structured data — starting with 2D-to-BIM conversion.',
    tagline: 'Less tracing and searching, more designing and building.',
    lead:
      'We built ArchiML, which reads 2D architectural drawings and produces structured BIM models with walls, doors, windows and staircases. The same skills — reading drawings, documents and images — apply across the whole building lifecycle.',
    visual: { shape: 'ring', colors: ['#1e293b', '#e7ff02', '#f59e0b'] },
    statement: 'Built for practices that live in drawings and documents.',
    intro: [
      'Architecture and construction run on documents that computers struggle to read: PDF and paper plans, scanned specifications, site photos and email threads. Teams spend days redrawing old plans before a renovation, searching specifications for a clause, or checking progress by walking the site.',
      'We use computer vision and language models to turn those documents into structured data — with a person checking the result against the real building. The goal is to remove the tracing and the searching, never the professional judgement.',
    ],
    solutions: [
      { title: 'Drawings to BIM', text: 'Recognising walls, doors, windows and staircases on 2D drawings and producing a structured BIM model exported to IFC.' },
      { title: 'Specification & document search', text: 'An assistant that answers questions across specifications, standards and project documents, citing the page it used.' },
      { title: 'Take-off assistance', text: 'Counting and measuring elements on drawings to speed up quantity take-off and early cost estimates.' },
      { title: 'Site photo analysis', text: 'Computer vision on site photos to track progress, spot missing elements or check safety equipment.' },
      { title: 'Project & client portals', text: 'Web and mobile tools where clients, contractors and consultants share drawings, approvals and progress.' },
      { title: 'BIM data pipelines', text: 'Extracting schedules and quantities from IFC models into the spreadsheets and systems your team already uses.' },
    ],
    sectors: [
      { title: 'Architecture practices', text: 'Faster starting points for renovation and survey work.' },
      { title: 'Engineering & surveying firms', text: 'Structured models and data from legacy drawings.' },
      { title: 'Developers & contractors', text: 'Document search, take-off help and site progress tracking.' },
      { title: 'Facility management', text: 'Up-to-date building models from the plans you already hold.' },
    ],
    reasons: [
      { title: 'A shipped drawing-to-BIM product', text: 'ArchiML is our own product: we know how messy real drawings are and where recognition needs a human check.' },
      { title: 'Open formats', text: 'Output in IFC and other open formats, so results work in the BIM tools your team already uses.' },
      { title: 'Human in the loop', text: 'Every automated step is reviewable and correctable before it becomes part of a model or a report.' },
      { title: 'Prototype on your drawings', text: 'We test on a sample of your own drawings first, so you see real results before committing.' },
    ],
    outcomes: [
      { title: 'Days of tracing removed', text: 'A structured starting model generated from existing drawings instead of redrawn by hand.' },
      { title: 'Clauses found in seconds', text: 'Specification questions answered with the exact page and section.' },
      { title: 'Earlier cost estimates', text: 'Quantities from drawings available sooner in the project.' },
      { title: 'Progress you can see', text: 'Site status from photos rather than from memory and phone calls.' },
    ],
    projects: ['ArchiML'],
    services: ['computer-vision', 'ai-development', 'rag'],
    faq: [
      {
        question: 'Which drawing formats can be converted?',
        answer: 'PDF and image scans of 2D plans, and DWG drawings. Clean vector drawings give the best results; scans work too, with more checking needed.',
      },
      {
        question: 'How accurate is automatic BIM conversion?',
        answer: 'It depends on drawing quality. We measure it on a sample of your own drawings before you commit, and the workflow always includes a human review step before a model is used.',
      },
      {
        question: 'Which BIM tools does the output work with?',
        answer: 'The models are exported as IFC, the open BIM standard, which Revit, ArchiCAD and most other BIM tools can import.',
      },
    ],
  },
  {
    slug: 'fintech',
    name: 'Fintech & Payments',
    title: 'Software development for fintech and payments',
    seoTitle: 'Fintech & Payments Software Development',
    seoDescription:
      'Fintech software development: payment and merchant platforms, fraud and anomaly detection, risk scoring, document processing and support assistants.',
    summary:
      'Payment and merchant platforms, fraud and risk models, and document automation — engineered for reliability and audit.',
    tagline: 'Money moves fast. The software behind it has to be right.',
    lead:
      'We have worked on engineering for Netevia, a US payments and banking platform for merchants. We bring that same care for correctness and security to platforms, risk models and automation for financial companies.',
    visual: { shape: 'loop', colors: ['#14532d', '#e7ff02', '#34d399'] },
    statement: 'Built for teams where a bug has a price tag.',
    intro: [
      'Financial products carry a higher bar than most software: every transaction must reconcile, every decision may be audited, and security is part of the product rather than an add-on. At the same time, customers expect the speed and ease of consumer apps.',
      'We build fintech software with that balance in mind — clean, tested services for money movement, and machine learning where it earns its place: spotting unusual transactions, scoring risk, and reading the documents that onboarding and compliance depend on. Every model comes with the explanations and logs an auditor will ask for.',
    ],
    solutions: [
      { title: 'Payment & merchant platforms', text: 'Merchant consoles, onboarding flows, card acceptance integrations and account dashboards, built and tested for correctness.' },
      { title: 'Fraud & anomaly detection', text: 'Models that flag unusual transactions and behaviour early, with reasons your analysts can review.' },
      { title: 'Credit & risk scoring', text: 'Scoring models on your historical data, with explainability for credit decisions and regular monitoring for drift.' },
      { title: 'KYC & document processing', text: 'Extracting data from IDs, statements and company documents, with confidence checks and human review.' },
      { title: 'Support assistants', text: 'Assistants that answer account and product questions from your help content, and escalate anything sensitive.' },
      { title: 'Reporting & data platforms', text: 'Reliable pipelines and dashboards for finance, risk and compliance teams.' },
    ],
    sectors: [
      { title: 'Payment providers', text: 'Merchant platforms, onboarding and transaction monitoring.' },
      { title: 'Banks & lenders', text: 'Risk scoring, document automation and customer assistants.' },
      { title: 'Insurance', text: 'Claims document processing and fraud signals.' },
      { title: 'Fintech startups', text: 'From MVP to a platform that passes due diligence.' },
    ],
    reasons: [
      { title: 'Payments experience', text: 'Engineering work on a US payments and banking platform for merchants.' },
      { title: 'Explainable models', text: 'Risk and fraud models that show why they flagged something, so decisions can be reviewed and defended.' },
      { title: 'Security first', text: 'Least-privilege access, encryption, audit logs and code review as standard, not as extras.' },
      { title: 'Testing that matches the stakes', text: 'Automated tests around every money-moving path, and reconciliation checks in production.' },
    ],
    outcomes: [
      { title: 'Fraud caught earlier', text: 'Suspicious activity flagged for review before it becomes a loss.' },
      { title: 'Faster onboarding', text: 'Documents read and checked automatically, with people reviewing only the unclear cases.' },
      { title: 'Decisions you can explain', text: 'Every automated score comes with the reasons behind it.' },
      { title: 'Fewer support tickets', text: 'Routine account questions answered instantly, sensitive ones routed to staff.' },
    ],
    projects: ['Netevia'],
    services: ['ai-development', 'data-engineering', 'web-development'],
    faq: [
      {
        question: 'How do you make ML models explainable for regulators?',
        answer: 'We prefer models whose drivers can be shown per decision, add explanation methods where a more complex model is justified, and log inputs, outputs and versions so any decision can be reconstructed later.',
      },
      {
        question: 'Can you work within our security requirements?',
        answer: 'Yes. We work in your cloud accounts and repositories under your access policies, follow least-privilege access, and sign an NDA and data processing agreement before any data is shared.',
      },
      {
        question: 'Do you build full platforms or only AI features?',
        answer: 'Both. We build web and mobile platforms end to end, and add machine learning where it clearly improves a decision.',
      },
    ],
  },
  {
    slug: 'ecommerce-retail',
    name: 'E-commerce & Retail',
    title: 'AI and software for e-commerce and retail',
    seoTitle: 'E-commerce & Retail AI Development',
    seoDescription:
      'AI for e-commerce and retail: product recommendations, search that understands intent, demand forecasting, product content generation and shopping assistants.',
    summary:
      'Recommendations, search that understands shoppers, demand forecasting and AI-written product content.',
    tagline: 'Help shoppers find the right product — and have it in stock.',
    lead:
      'Online stores and retail chains already hold years of behaviour, search and sales data. We turn it into better search, more relevant recommendations and forecasts that keep the right stock in the right place.',
    visual: { shape: 'weave', colors: ['#7c2d12', '#e7ff02', '#fb923c'] },
    statement: 'Built for stores that want to sell smarter, not louder.',
    intro: [
      'In e-commerce, small improvements compound: a search that understands “warm winter jacket for kids”, a recommendation that fits what someone just bought, a forecast that avoids an empty shelf. Each one is a model trained on data you already collect.',
      'We start with the metric you care about — conversion, basket size, stock-outs or content cost — build the simplest model that moves it, and test it against your current setup before rolling it out to every customer.',
    ],
    solutions: [
      { title: 'Recommendations', text: 'Personalised “you may also like” and next-best-product suggestions from browsing and purchase history.' },
      { title: 'Search that understands intent', text: 'Semantic search that finds the right products for natural-language queries and typos, in several languages.' },
      { title: 'Demand forecasting', text: 'Forecasts per product and store that feed purchasing and replenishment.' },
      { title: 'Product content generation', text: 'Drafting product descriptions, translations and attributes at scale, reviewed before publishing.' },
      { title: 'Shopping assistants', text: 'Chat assistants that ask the right questions and recommend suitable products, on the site or in messengers.' },
      { title: 'Customer analytics', text: 'Segmentation, churn signals and review analysis that show what to improve next.' },
    ],
    sectors: [
      { title: 'Online stores', text: 'Search, recommendations and content at scale.' },
      { title: 'Marketplaces', text: 'Matching buyers with the right sellers and products.' },
      { title: 'Retail chains', text: 'Store-level forecasting and stock planning.' },
      { title: 'Consumer brands', text: 'Product content, reviews and direct-to-consumer apps.' },
    ],
    reasons: [
      { title: 'Metric first', text: 'Every model is tied to a business number and tested against your current setup before a full rollout.' },
      { title: 'Multilingual by default', text: 'Search and content that work in Georgian, English, Russian and other languages your customers use.' },
      { title: 'Fits your platform', text: 'Integrations with Shopify, WooCommerce, Magento or a custom store through APIs, without replatforming.' },
      { title: 'You own the models', text: 'Code, models and data pipelines are handed over, with no lock-in to a recommendation vendor.' },
    ],
    outcomes: [
      { title: 'More relevant results', text: 'Shoppers find what they meant, not only what they typed.' },
      { title: 'Bigger baskets', text: 'Recommendations that fit the customer and the moment.' },
      { title: 'Fewer stock-outs', text: 'Purchasing guided by forecasts instead of last year’s numbers.' },
      { title: 'Content at scale', text: 'Product pages drafted in minutes and reviewed by your team.' },
    ],
    projects: [],
    services: ['ai-development', 'nlp-chatbots', 'generative-ai'],
    faq: [
      {
        question: 'How much data do we need for recommendations?',
        answer: 'A few months of browsing and purchase history is usually enough to beat simple “bestsellers” lists. We check your data first and say plainly if it is not enough yet.',
      },
      {
        question: 'Will it work with our e-commerce platform?',
        answer: 'Yes. We integrate through your platform’s APIs — Shopify, WooCommerce, Magento or a custom store — so there is no need to replatform.',
      },
      {
        question: 'How do we know it actually works?',
        answer: 'We run it as an A/B test against your current setup on a share of traffic and compare conversion, basket size or the metric agreed at the start before rolling it out fully.',
      },
    ],
  },
]

export const getIndustry = (slug) => industries.find((industry) => industry.slug === slug)

export default industries
